import { firstValueFrom } from 'rxjs';
import DataConnector from '@highcharts/dashboards/es-modules/Data/Connectors/DataConnector.js';
import type { Event as DataConnectorEvent } from '@highcharts/dashboards/es-modules/Data/Connectors/DataConnector.js';
import type { DataConnectorOptions } from '@highcharts/dashboards/es-modules/Data/Connectors/DataConnectorOptions.js';
import type {
    DashboardCellValue,
    DashboardQueryMeta,
    DashboardQueryResult,
} from '@h20/contracts/types/api/dashboard/DashboardData';
import { PROVIDER_CONNECTOR_TYPE } from '@h20/contracts/types/api/dashboard/DashboardLayout';
import type { DashboardProviderConnector } from '@h20/contracts/types/api/dashboard/DashboardLayout';
import type { DashboardDataProvider } from './dashboard-provider.types';
import type { DashboardDataErrorReason, DashboardDataState } from '../state/dashboard-data-state';
import type { DashboardQueryKey } from '@h20/contracts/types/api/dashboard/DashboardQueryCatalog';

/** What a board stores about one server-fed dataset, as the connector reads it back. */
type ProviderConnectorOptions = DataConnectorOptions &
    Pick<DashboardProviderConnector, 'type' | 'queryKey' | 'params'>;

class ProviderConnector extends DataConnector {
    declare readonly options: ProviderConnectorOptions;

    /** The provider lookup every instance uses on `load()`; wired by `DashboardRegistryService`. */
    static resolveProvider?: (
        id: string,
        queryKey: DashboardQueryKey
    ) => DashboardDataProvider | undefined;

    private loadState: DashboardDataState = { status: 'loading' };
    private loadedMeta?: DashboardQueryMeta;

    static {
        DataConnector.registerType(PROVIDER_CONNECTOR_TYPE, ProviderConnector);
    }

    /** How the last `load()` went */
    get state(): DashboardDataState {
        return this.loadState;
    }

    /** What the last successful `load()` was told about its rows. */
    get meta(): DashboardQueryMeta | undefined {
        return this.loadedMeta;
    }

    override async load(eventDetail?: DataConnectorEvent['detail']): Promise<this> {
        this.loadState = { status: 'loading' };
        this.emit({ type: 'load', detail: eventDetail });

        const provider = ProviderConnector.resolveProvider?.(
            this.options.id,
            this.options.queryKey
        );
        if (!provider) {
            return this.fail(
                'missing-provider',
                `no data provider is registered for connector '${this.options.id}'; pass it via the ` +
                    '<pulse-dashboard [dataProviders]> input before the board is built',
                eventDetail
            );
        }

        let result: DashboardQueryResult;
        try {
            result = await firstValueFrom(provider.load());
        } catch (cause) {
            return this.fail('load-failed', cause, eventDetail);
        }

        this.loadedMeta = result.meta;
        await this.fillTables(result);
        this.loadState = { status: result.rows.length > 0 ? 'ready' : 'empty' };
        this.emit({ type: 'afterLoad', detail: eventDetail });
        return this;
    }

    /** Records the failure, empties the tables and still resolves */
    private async fail(
        reason: DashboardDataErrorReason,
        cause: unknown,
        eventDetail?: DataConnectorEvent['detail']
    ): Promise<this> {
        this.loadState = { status: 'error', reason };
        this.loadedMeta = undefined;
        await this.fillTables({ columns: [], rows: [] });
        console.warn(`pulse-dashboard: connector '${this.options.id}' loaded no data -`, cause);
        this.emit({ type: 'loadError', detail: eventDetail });
        return this;
    }

    /** Replaces every managed table's columns with the dataset, in declared column order; a key a row omits reads as null. */
    private async fillTables(result: DashboardQueryResult): Promise<void> {
        const columns: Record<string, DashboardCellValue[]> = {};
        for (const column of result.columns) {
            columns[column.key] = result.rows.map((row) => row[column.key] ?? null);
        }

        for (const table of Object.values(this.dataTables)) {
            table.deleteColumns();
            table.setColumns(columns);
        }
        await this.applyTableModifiers();
    }
}

/** Registers `Provider` with Dashboards' connector-type registry at the type level. */
declare module '@highcharts/dashboards/es-modules/Data/Connectors/DataConnectorType' {
    interface DataConnectorTypes {
        Provider: typeof ProviderConnector;
    }
}

export { ProviderConnector };
